import { useCallback, useEffect, useRef, useState } from "react";
import { getOnboardingProgressApi } from "../../../api/userApi";
import { getBatchStatusApi, submitBatchApi } from "../../../api/enrichmentApi";
import {
  ONBOARDING_ACTION_MODEL,
  ONBOARDING_ACTION_PROVIDER,
  ONBOARDING_ACTION_ROW_LIMIT,
  clearOnboardingActionRun,
  planOnboardingActionRun,
  readOnboardingActionRun,
  recordOnboardingActionSubmission,
} from "../../../helper/onboardingActions";
import { runOnboardingActions } from "./runOnboardingActions";

const RECOVERY_ERROR = "We could not load your onboarding actions. Reload the page to try again.";
const wait = (delayMs) => new Promise((resolve) => window.setTimeout(resolve, delayMs));

/**
 * Runs the wizard's selected actions on the saved search the wizard created.
 * Starts only when this tab requested a run (Finish in the wizard), and
 * restores accepted batches from GET /user/onboarding/ so a reload resumes
 * without resubmitting.
 */
export function useOnboardingActionRun({ workspaceId, companyIds, onBatchSettled }) {
  const [requested] = useState(() => Boolean(readOnboardingActionRun(workspaceId)));
  const [run, setRun] = useState(null);
  const companyIdsRef = useRef(companyIds);
  const onBatchSettledRef = useRef(onBatchSettled);
  companyIdsRef.current = companyIds;
  onBatchSettledRef.current = onBatchSettled;
  const ready = requested && companyIds.length > 0;

  useEffect(() => {
    if (!ready) return undefined;
    let cancelled = false;
    const isCancelled = () => cancelled;
    const rowIds = companyIdsRef.current.slice(0, ONBOARDING_ACTION_ROW_LIMIT);

    (async () => {
      const progress = await getOnboardingProgressApi();
      if (cancelled) return;
      if (progress?.status !== 200) {
        setRun({ items: [], error: RECOVERY_ERROR, finished: true });
        return;
      }
      const plan = planOnboardingActionRun(progress.data, workspaceId, readOnboardingActionRun(workspaceId));
      if (plan.actions.length === 0) {
        clearOnboardingActionRun();
        return;
      }
      const result = await runOnboardingActions({
        ...plan,
        submit: (action) => submitBatchApi({
          company_ids: rowIds,
          query: action.query,
          llm_provider: ONBOARDING_ACTION_PROVIDER,
          model: ONBOARDING_ACTION_MODEL,
          workspace_id: workspaceId,
        }),
        getStatus: getBatchStatusApi,
        wait,
        isCancelled,
        onProgress: (items) => setRun({ items, error: null, finished: false }),
        onAccepted: (action, batchId) => recordOnboardingActionSubmission(workspaceId, action.query, batchId),
        onBatchSettled: (batchId) => onBatchSettledRef.current?.(batchId),
      });
      if (result.outcome === "cancelled") return;
      clearOnboardingActionRun();
      setRun((current) => ({ items: current?.items || [], error: result.error, finished: true }));
    })();

    return () => {
      cancelled = true;
    };
  }, [ready, workspaceId]);

  const onDismiss = useCallback(() => setRun(null), []);
  return { run, onDismiss };
}
